import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCharacterizationDate } from "../../characterization-2/core/utils/utils";
import {
  getZoneDisplayLines,
  getAppZoom,
  resolveSuperContainerForExport,
} from "./clientUtils";
import { exportPdfFromRow } from "../../characterization-2/core/utils/exportPdfFromRow";
import { exportCsvFromRow } from "../../characterization-2/core/utils/exportCsvFromRow";
import { exportEmailFromRow } from "../../characterization-2/core/utils/exportEmailFromRow";
import PdfExportProgressOverlay from "../../../components/loader/PdfExportProgressOverlay";
import PdfModal from "../../../components/PdfModal";
import exporter from "../../../static/assets/Exporter.svg";

const STATUS_VARIANTS = {
  Validated: {
    icon: "fas fa-check",
    badgeClass: "char-badge-validated",
  },
  Validé: {
    icon: "fas fa-check",
    badgeClass: "char-badge-validated",
  },
  ImageEnTraitement: {
    icon: "fas fa-history",
    badgeClass: "char-badge-progress",
  },
  CorrectionEnTraitement: {
    icon: "fas fa-tasks",
    badgeClass: "char-badge-correction",
  },
  Erreur: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "char-badge-error",
  },
  Error: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "char-badge-error",
  },
  ACompleter: {
    icon: "fas fa-pen",
    badgeClass: "char-badge-tocomplete",
  },
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const variant = STATUS_VARIANTS[status] ?? {
    icon: "fas fa-question-circle",
    badgeClass: "char-badge-outline",
  };
  const statusLabel =
    status === "ImageEnTraitement"
      ? t("Image en traitement")
      : status === "CorrectionEnTraitement"
        ? t("Correction en traitement")
        : status === "Validated" || status === "Validé"
          ? t("Validated")
          : t(status);
  return (
    <span className={`char-badge ${variant.badgeClass}`}>
      <i className={`${variant.icon} text-xs text-current`} />
      {statusLabel}
    </span>
  );
};

const ClientHistoryRow = ({ row, containers, superContainers = [] }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const exportButtonRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    percent: 0,
    label: "",
  });
  const [pdfFile, setPdfFile] = useState([]);
  const [openModalExport, setOpenModalExport] = useState(false);
  const [initialPdfModalAction, setInitialPdfModalAction] = useState("export");

  const zone = row.zoneSite;
  const client = row.clientParsed ?? { name: "-", address: "-" };
  const superContainer = resolveSuperContainerForExport(
    row.superContainerId,
    superContainers,
  );
  const wasteName = row?.wasteCategory?.wasteCategoryName ?? null;
  const weightNum = row?.weight != null ? Number(row.weight) : NaN;
  const canExport =
    Boolean(client?.name && String(client.name).trim() !== "-") &&
    !Number.isNaN(weightNum) &&
    weightNum > 0 &&
    Boolean(wasteName && String(wasteName).trim() !== "-");

  const startLabel = row.startDate
    ? formatCharacterizationDate(row.startDate)
    : "—";
  const lastUpdateLabel = row.lastUpdateDate
    ? formatCharacterizationDate(row.lastUpdateDate)
    : "—";
  const { siteName: zoneSiteName, subLabel: zoneSubLabel } =
    getZoneDisplayLines(zone);

  const updateMenuPosition = useCallback(() => {
    if (!exportButtonRef.current) return;
    const rect = exportButtonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (
        exportButtonRef.current?.contains(event.target) ||
        event.target.closest(".char-dropdown-export-portal")
      ) {
        return;
      }
      setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleExportPdf = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportProgress({ percent: 0, label: t("Loading...") });
    try {
      await exportPdfFromRow({
        period: row,
        container: zone,
        superContainer,
        client,
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setIsExporting(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };

  const handleExportCsv = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportProgress({ percent: 5, label: t("Loading...") });
    try {
      await exportCsvFromRow({
        period: row,
        container: zone,
        superContainer,
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setIsExporting(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };

  const handleExportEmail = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    try {
      await exportEmailFromRow({
        period: row,
        container: zone,
        superContainer,
        client,
        i18n,
        t,
        setPdfFile,
        setOpenModalExport,
        setInitialPdfModalAction,
        selectedFilesForExport: [],
      });
    } catch (error) {
      console.error("Error exporting email:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <article
      className="table-standard-row cursor-pointer"
      onClick={() => {
        history.push(`/characterization/details/${zone._id}`, {
          period: row,
          client,
          container: zone,
          superContainer,
          containers,
          zone,
          listSearch: location.search || "",
        });
      }}
    >
      <div className="table-standard-row-cell">
        <span className="table-standard-row-cell-truncate">{startLabel}</span>
      </div>
      <div className="table-standard-row-cell">
        <StatusBadge status={row.status} />
      </div>
      <div className="table-standard-row-cell min-w-0 !items-start">
        <div className="flex flex-col gap-0.5 min-w-0 w-full py-0.5">
          <span
            className="text-sm font-medium text-[#222222] truncate"
            title={zoneSiteName}
          >
            {zoneSiteName}
          </span>
          {zoneSubLabel ? (
            <span
              className="text-[13px] text-[#9CA3AF] truncate"
              title={zoneSubLabel}
            >
              {zoneSubLabel}
            </span>
          ) : null}
        </div>
      </div>
      <div className="table-standard-row-cell">
        <span className="table-standard-row-cell-truncate">
          {lastUpdateLabel}
        </span>
      </div>
      <div
        className="table-standard-row-cell table-standard-row-actions"
        data-label=""
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={exportButtonRef}
          type="button"
          className="char-button char-button--table-action"
          disabled={isExporting || !canExport}
          aria-expanded={isDropdownOpen}
          onClick={(e) => {
            e.stopPropagation();
            if (!canExport) return;
            if (!isDropdownOpen) {
              updateMenuPosition();
            }
            setIsDropdownOpen(!isDropdownOpen);
          }}
        >
          <img src={exporter} alt="" className="char-button-icon" />
          {t("Export")}
        </button>
        {isDropdownOpen &&
          createPortal(
            <div
              className="char-dropdown char-dropdown--compact char-dropdown-export-portal"
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                right: "auto",
                marginTop: 3,
                zIndex: 9999,
                transform: `translateX(-100%) scale(${getAppZoom()})`,
                transformOrigin: "top right",
                width: "350px",
              }}
            >
              <button
                type="button"
                onClick={handleExportPdf}
                className="char-dropdown-item"
              >
                <img
                  src={exporter}
                  alt=""
                  className="char-dropdown-item-icon"
                />
                <span>{t("PDF")}</span>
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                className="char-dropdown-item"
              >
                <img
                  src={exporter}
                  alt=""
                  className="char-dropdown-item-icon"
                />
                <span>{t("CSV")}</span>
              </button>
              <div className="char-dropdown-item-divider" />
              <button
                type="button"
                onClick={handleExportEmail}
                className="char-dropdown-item"
              >
                <i className="fas fa-envelope char-text-secondary" />
                <span>{t("Send by email")}</span>
              </button>
            </div>,
            document.body,
          )}
      </div>
      {isExporting && (
        <PdfExportProgressOverlay
          percent={exportProgress.percent}
          label={exportProgress.label}
        />
      )}
      {openModalExport && (
        <PdfModal
          data={{
            ...zone,
            wasteName: row.wasteCategory?.wasteCategoryName ?? "-",
            positioning: zone?.positioning || "",
          }}
          setOpenModalExport={setOpenModalExport}
          openModalExport={openModalExport}
          setLoadScreen={setIsExporting}
          setPdfFile={setPdfFile}
          pdfFile={pdfFile}
          characterization={true}
          pdfDateTime={new Date(row?.start || row?._time || new Date())}
          initialAction={initialPdfModalAction}
        />
      )}
    </article>
  );
};

export default ClientHistoryRow;
